import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Button, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  Box, AppBar, Toolbar, IconButton, CssBaseline,
  Menu, MenuItem, ButtonBase, CircularProgress
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import styled, { keyframes, createGlobalStyle, css } from "styled-components";
import recruiterTheme, {
  FONT_BODY, FONT_DISPLAY, TYPE, RADIUS, SHADOW, PAGE_PADDING, ELLIPSIS, ELLIPSIS_CSS,
} from "../../styles/recruiterTheme";

import {
  PendingActions as PendingActionsIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  BarChart as BarChartIcon,
  Widgets as WidgetsIcon,
  ExitToApp as ExitToAppIcon,
} from "@mui/icons-material";
import NotificationsIcon from "@mui/icons-material/Notifications";

import ProfileForm from "../shared/Profileform.js";
import CandidateProfiles from "../shared/CandidateProfile.js";
import CandidateData from "../shared/CandidateData.js";
import Pagination from "../../utils/Pagination.js";
import JDDetails from "../shared/JDDetails.js";
import FeedbackForm from "./FeedBackForm.js";
import logo from "../../styles/logo.png";
import PendingData from "../shared/PendingData.js";
import Reports from "../shared/Reports";

// ─── Constants ────────────────────────────────────────────────────────────────
const ITEMS_PER_PAGE = 10;
const PENDING_STATUSES = ["Interview_Scheduled", "Round2_Scheduled"];

// ─── Global styles (fonts load from public/index.html) ────────────────────────
const GlobalStyle = createGlobalStyle`
  * { box-sizing: border-box; }
  @keyframes fadeSlideIn {
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const floatDot = keyframes`
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-3px); }
`;

// ─── Layout ───────────────────────────────────────────────────────────────────
const Container = styled.div`
  display: flex;
  background: #f3eef9;
  height: 100vh;          /* CHANGED from min-height — the actual fix */
  font-family: ${FONT_BODY};
  padding-top: 72px;
  overflow: hidden;
`;

const CenteredSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
  background: #f3eef9;
`;

const ScrollableContent = styled.div`
  width: 100%;
  min-height: 100%;
  padding-bottom: 40px;
`;

// ─── Sidebar (same metrics as the recruiter sidebar) ─────────────────────────
const MenuContainer = styled.nav`
  display: flex;
  flex-direction: column;
  width: 300px;
  min-width: 260px;
  flex-shrink: 0;
  height: 100%;
  padding: 24px 16px;
  overflow-y: auto;
  background: linear-gradient(170deg, #2d1b5e 0%, #4f378b 55%, #6750a4 100%);
  box-shadow: 4px 0 24px rgba(79, 55, 139, 0.18);
  z-index: 10;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.16);
    border-radius: 3px;
  }
`;

const SidebarLabel = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.overline}px;
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.45);
  margin: 16px 0 8px 12px;
`;

const NavButton = styled(ButtonBase)`
  && {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 12px;
    width: 100%;
    padding: 10px 12px;
    margin-bottom: 4px;
    border-radius: ${RADIUS.sm}px;
    position: relative;
    text-align: left;
    font-family: ${FONT_BODY};
    font-size: ${TYPE.body}px;
    font-weight: 600;
    line-height: 1.25;
    color: rgba(255, 255, 255, 0.82);
    transition: background 0.18s ease, color 0.18s ease;

    /* Fixed-width icon column keeps every label on the same x. */
    > svg {
      font-size: 20px;
      width: 20px;
      flex-shrink: 0;
      opacity: 0.88;
    }

    /* The label takes the free width; the count badge sits after it. */
    > .nav-label {
      flex: 1;
      min-width: 0;
      ${ELLIPSIS_CSS}
    }

    &:hover {
      background: rgba(255, 255, 255, 0.14);
      color: #fff;
    }

    ${(p) => p.$active && css`
      background: rgba(255, 255, 255, 0.16);
      color: #fff;
      font-weight: 700;
      > svg { opacity: 1; }
      &::before {
        content: "";
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        width: 3px;
        height: 20px;
        border-radius: 0 3px 3px 0;
        background: #d0bcff;
      }
    `}

    ${(p) => p.$danger && css`
      &:hover {
        background: rgba(255, 107, 107, 0.16);
        color: #ffd9d9;
      }
    `}
  }
`;

/* Small round count badge, vertically centred at the row's right edge. */
const NavBadge = styled.div`
  flex: 0 0 auto;
  margin-left: auto;
  min-width: 20px;
  height: 20px;
  padding: 0 4px;
  border-radius: 10px;
  background: linear-gradient(135deg, #ff6b6b, #ff4444);
  color: #fff;
  font-family: ${FONT_BODY};
  font-size: ${TYPE.overline}px;
  font-weight: 700;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(255, 68, 68, 0.5);
  font-variant-numeric: tabular-nums;
`;

const SidebarDivider = styled.div`
  width: 100%;
  height: 1px;
  flex-shrink: 0;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  margin: 12px 0;
`;

// ─── AppBar ───────────────────────────────────────────────────────────────────
const StyledAppBar = styled(AppBar)`
  && {
    background: rgba(255, 255, 255, 0.97) !important;
    backdrop-filter: blur(20px);
    border-bottom: 1px solid rgba(103, 80, 164, 0.12) !important;
    box-shadow: 0 2px 20px rgba(79, 55, 139, 0.08) !important;
    left: 0 !important;
    width: 100% !important;
    z-index: 1300 !important;
  }
`;

const BrandText = styled(Typography)`
  && {
    font-family: ${FONT_DISPLAY} !important;
    font-weight: 800 !important;
    font-size: ${TYPE.section}px !important;
    background: linear-gradient(135deg, #2d1b5e 0%, #6750a4 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    letter-spacing: -0.02em;
    line-height: 1.25;
  }
`;

const StyledAvatar = styled.div`
  font-family: ${FONT_BODY};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #7c5cbf, #4f378b);
  color: #fff;
  font-size: ${TYPE.body}px;
  font-weight: 700;
  border-radius: 50%;
  box-shadow: 0 3px 10px rgba(79, 55, 139, 0.35);
  flex-shrink: 0;
`;

const BellButton = styled(IconButton)`
  && {
    background: linear-gradient(135deg, #f3eef9, #e8def8);
    border-radius: ${RADIUS.sm}px;
    width: 40px;
    height: 40px;
    transition: background 0.2s;
    position: relative;
    &:hover {
      background: linear-gradient(135deg, #e8def8, #d0bcff);
    }
    svg {
      color: #4f378b;
      font-size: 20px;
    }
  }
`;

// ─── Page shell ("cards on canvas") ───────────────────────────────────────────
// Transparent padded container: the tab sits on the lavender canvas and each
// section inside is its own white card.
const StyledPaper = styled.div`
  padding: ${PAGE_PADDING}px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  ${css`animation: ${fadeSlideIn} 0.4s ease both;`}
`;

// White card around a table; overflow-x auto is only a fallback.
const TableCard = styled.div`
  background: #fff;
  border: 1px solid #ede7f6;
  border-radius: ${RADIUS.lg}px;
  box-shadow: ${SHADOW.card};
  overflow: hidden;
  min-width: 0;
`;

const TableScroll = styled.div`
  width: 100%;
  overflow-x: auto;
`;

// Filter toolbar above the table, inside the same card.
const CardToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 16px 24px;
  border-bottom: 1px solid #ede7f6;
`;

// Single-line cell text with a max width (pair with title= for the tooltip).
const CellEllipsis = styled.div`
  ${ELLIPSIS_CSS}
  max-width: ${(p) => p.maxWidth || 200}px;
`;

// Two-line clamp for long free text (role titles).
const CellClamp = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.5;
  word-break: break-word;
  ${(p) => (p.minWidth ? `min-width: ${p.minWidth}px;` : "")}
  ${(p) => (p.maxWidth ? `max-width: ${p.maxWidth}px;` : "")}
`;

// ─── Page Title ───────────────────────────────────────────────────────────────
const PageTitle = styled(Typography)`
  && {
    font-family: ${FONT_DISPLAY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.page}px !important;
    line-height: 1.25 !important;
    letter-spacing: -0.02em;
    background: linear-gradient(135deg, #2d1b5e 0%, #6750a4 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin: 0 !important;
  }
`;

const PageSubtitle = styled(Typography)`
  && {
    font-family: ${FONT_BODY} !important;
    font-size: ${TYPE.body}px !important;
    line-height: 1.5 !important;
    color: #8b7db5 !important;
    font-weight: 500 !important;
    margin: 4px 0 24px !important;
  }
`;

// ─── Stat Cards ───────────────────────────────────────────────────────────────
// $fill: keep a lone card at one column's width instead of stretching it.
const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${(p) => (p.$fill ? "auto-fill" : "auto-fit")}, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const StatCard = styled.div`
  background: ${(p) => p.gradient || "linear-gradient(135deg, #6750A4, #4F378B)"};
  border-radius: ${RADIUS.md}px;
  padding: 16px 24px;
  min-height: 96px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  color: #fff;
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 24px ${(p) => p.shadow || "rgba(103,80,164,0.28)"};
  transition: transform 0.22s ease, box-shadow 0.22s ease;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 14px 32px ${(p) => p.shadow || "rgba(103,80,164,0.36)"};
  }
  &::after {
    content: "";
    position: absolute;
    top: -30px;
    right: -30px;
    width: 90px;
    height: 90px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
  }
`;

const StatText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  position: relative;
  z-index: 1;
`;

const StatNumber = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.stat}px;
  font-weight: 800;
  line-height: 1;
  font-variant-numeric: tabular-nums;
`;

const StatLabel = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.overline}px;
  font-weight: 700;
  letter-spacing: 0.06em;
  line-height: 1.25;
  opacity: 0.88;
  text-transform: uppercase;
`;

const StatIcon = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  opacity: 0.22;
  svg {
    font-size: 40px;
  }
`;

// ─── User Card ────────────────────────────────────────────────────────────────
const UserCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px 16px 4px 4px;
  background: linear-gradient(135deg, #f8f4ff, #ede7f6);
  border-radius: ${RADIUS.pill}px;
  border: 1px solid rgba(103, 80, 164, 0.15);
  min-width: 160px;
  white-space: nowrap;
`;

// ─── Table ────────────────────────────────────────────────────────────────────
const StyledTable = styled(Table)`
  && {
    border-collapse: separate;
    border-spacing: 0;
  }
  && tbody tr:last-child td {
    border-bottom: none !important;
  }
`;

const HeadCell = styled(TableCell)`
  && {
    background: linear-gradient(135deg, #4f378b 0%, #6750a4 100%);
    color: #fff !important;
    font-family: ${FONT_BODY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.overline}px !important;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    padding: 12px 16px !important;
    white-space: nowrap;
    vertical-align: middle;
    border-bottom: none !important;
  }
`;

const BodyRow = styled(TableRow)`
  && {
    transition: background 0.16s;
    &:hover td {
      background: #faf7ff;
    }
    &:nth-child(even) td {
      background: #fdfbff;
    }
    &:hover td {
      background: #f3eef9 !important;
    }
  }
`;

const BodyCell = styled(TableCell)`
  && {
    font-family: ${FONT_BODY} !important;
    font-size: ${TYPE.small}px !important;
    font-weight: 500 !important;
    color: #3d2b6b !important;
    padding: 12px 16px !important;
    vertical-align: middle;
    border-bottom: 1px solid #f0eaf9 !important;
  }
`;

// ─── Buttons ──────────────────────────────────────────────────────────────────
const GhostButton = styled(Button)`
  && {
    color: #6750a4 !important;
    font-family: ${FONT_BODY} !important;
    font-weight: 600 !important;
    font-size: ${TYPE.small}px !important;
    text-transform: none !important;
    border-radius: ${RADIUS.sm}px !important;
    height: 32px;
    padding: 0 12px !important;
    white-space: nowrap;
    transition: background 0.18s !important;
    &:hover {
      background: rgba(103, 80, 164, 0.08) !important;
    }
  }
`;

// margin-left -8px lines the link text up with the header text above it.
const LinkButton = styled(Button)`
  && {
    color: #6750a4 !important;
    font-family: ${FONT_BODY} !important;
    font-weight: 600 !important;
    font-size: ${TYPE.small}px !important;
    text-transform: none !important;
    text-decoration: underline !important;
    text-underline-offset: 3px !important;
    min-width: 0 !important;
    padding: 4px 8px !important;
    margin-left: -8px;
    justify-content: flex-start;
    white-space: nowrap;
    line-height: 1.5;
    border-radius: ${RADIUS.sm}px !important;
    &:hover {
      background: rgba(103, 80, 164, 0.06) !important;
    }
  }
`;

// ─── Status Chips / meet link (all 24px so they line up in a column) ─────────
const CompletedChip = styled.span`
  background: linear-gradient(135deg, #d4fae0, #b5eac3);
  color: #1b6b3a;
  font-family: ${FONT_BODY};
  font-weight: 700;
  font-size: ${TYPE.overline}px;
  height: 24px;
  padding: 0 12px;
  border-radius: ${RADIUS.pill}px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  &::before {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #2e9b54;
    ${css`
      animation: ${floatDot} 2s ease-in-out infinite;
    `}
    display: inline-block;
  }
`;

const ExpiredChip = styled.span`
  background: #ede7f6;
  color: #4f378b;
  font-family: ${FONT_BODY};
  font-weight: 700;
  font-size: ${TYPE.overline}px;
  height: 24px;
  padding: 0 12px;
  border-radius: ${RADIUS.pill}px;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
`;

const JoinLink = styled.a`
  color: #6750a4;
  font-family: ${FONT_BODY};
  font-weight: 600;
  font-size: ${TYPE.small}px;
  text-decoration: underline;
  text-underline-offset: 3px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
`;

// ─── Notification ─────────────────────────────────────────────────────────────
const NotifHeader = styled.div`
  padding: 16px 16px 8px;
  font-family: ${FONT_BODY};
  font-weight: 700;
  font-size: ${TYPE.body}px;
  line-height: 1.5;
  color: #2d1b5e;
  border-bottom: 1px solid #f0eaf9;
`;

const NotifItem = styled(MenuItem)`
  && {
    padding: 12px 16px !important;
    border-bottom: 1px solid #faf7ff !important;
    transition: background 0.15s !important;
    &:hover {
      background: #f8f4ff !important;
    }
  }
`;

const NotifDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ff6b6b, #ff4444);
  flex-shrink: 0;
  margin-top: 6px;
  box-shadow: 0 2px 6px rgba(255, 68, 68, 0.4);
`;

// ─── Section Header ───────────────────────────────────────────────────────────
const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const SectionAccent = styled.div`
  width: 4px;
  height: 24px;
  border-radius: 4px;
  flex-shrink: 0;
  background: linear-gradient(135deg, #7c5cbf, #4f378b);
`;

const SECTION_TITLE_SX = {
  fontFamily: FONT_BODY,
  fontWeight: 700,
  fontSize: TYPE.section,
  lineHeight: 1.25,
  color: "#2D1B5E",
};

// ─── Date Filter ──────────────────────────────────────────────────────────────
const FilterField = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
`;

const DateInput = styled.input`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.small}px;
  font-weight: 500;
  color: #3d2b6b;
  background: #f8f4ff;
  border: 1.5px solid #c4b0e8;
  border-radius: ${RADIUS.sm}px;
  height: 40px;
  padding: 0 12px;
  outline: none;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
  &:focus {
    border-color: #6750a4;
    box-shadow: 0 0 0 3px rgba(103, 80, 164, 0.12);
  }
  &::-webkit-calendar-picker-indicator {
    cursor: pointer;
    opacity: 0.6;
    filter: invert(30%) sepia(60%) saturate(400%) hue-rotate(220deg);
  }
`;

const FilterLabel = styled.span`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.overline}px;
  font-weight: 700;
  color: #8b7db5;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  height: 40px;
  line-height: 1;
`;

const FILTER_SUBLABEL_STYLE = {
  color: "#B0A0D0",
  textTransform: "none",
  letterSpacing: 0,
  fontSize: TYPE.caption,
  fontWeight: 600,
};

const ClearFilterBtn = styled(Button)`
  && {
    font-family: ${FONT_BODY} !important;
    font-size: ${TYPE.caption}px !important;
    font-weight: 600 !important;
    color: #6750a4 !important;
    text-transform: none !important;
    border: 1.5px solid #c4b0e8 !important;
    border-radius: ${RADIUS.sm}px !important;
    height: 40px;
    padding: 0 16px !important;
    background: #fff !important;
    &:hover {
      background: #ede7f6 !important;
    }
  }
`;

const FilterResultPill = styled.div`
  background: #ede7f6;
  color: #4a3380;
  border-radius: ${RADIUS.pill}px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  padding: 0 12px;
  font-size: ${TYPE.caption}px;
  font-weight: 700;
  font-family: ${FONT_BODY};
  white-space: nowrap;
`;

// ─── Inline style objects (defined outside component to avoid recreation) ─────
const TOOLBAR_STYLE = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  minHeight: 72,
  padding: "0 24px",
};
const LOGO_BOX_SX = {
  width: 48,
  height: 48,
  borderRadius: `${RADIUS.md}px`,
  background: "linear-gradient(135deg, #EDE7F6, #D0BCFF)",
  border: "2px solid rgba(103,80,164,0.18)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 4px 16px rgba(79,55,139,0.18)",
  flexShrink: 0,
};
const LOGO_IMG_STYLE = { height: 36, width: 36, borderRadius: RADIUS.sm };
const PLATFORM_LABEL_SX = {
  fontFamily: FONT_BODY,
  fontSize: TYPE.overline,
  fontWeight: 700,
  color: "#8B7DB5",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  lineHeight: 1.25,
  mt: "4px",
};
const NOTIF_BADGE_SX = {
  position: "absolute",
  top: 4,
  right: 4,
  minWidth: 16,
  height: 16,
  borderRadius: "8px",
  background: "linear-gradient(135deg, #FF6B6B, #FF4444)",
  color: "#fff",
  fontSize: TYPE.overline - 1,
  fontWeight: 700,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  px: "3px",
  boxShadow: "0 2px 6px rgba(255,68,68,0.5)",
  border: "1.5px solid #fff",
  lineHeight: 1,
  pointerEvents: "none",
};
const NOTIF_MENU_PAPER_SX = {
  width: 380,
  maxHeight: 420,
  borderRadius: `${RADIUS.lg}px`,
  boxShadow: "0 12px 40px rgba(79,55,139,0.18)",
  border: "1px solid #EDE7F6",
  mt: 1,
};
const NOTIF_EMPTY_SX = {
  p: "24px",
  textAlign: "center",
  color: "#8B7DB5",
  fontFamily: FONT_BODY,
  fontSize: TYPE.body,
};
const EMPTY_TEXT_SX = {
  fontFamily: FONT_BODY,
  color: "#B0A0D0",
  fontSize: TYPE.body,
  lineHeight: 1.5,
};
const MUTED_CELL_SX = {
  fontSize: `${TYPE.caption}px !important`,
  color: "#8B7DB5 !important",
};

// ─── Tab / storage helpers ───────────────────────────────────────────────────
// Tabs that survive a refresh. Transient sub-views (JD details, candidate
// profiles/data, feedback form) are not persisted; on refresh the user lands
// back on the tab they were opened from.
const PERSISTABLE_TABS = new Set([
  "InterviewerDashboard", "pendingData", "scheduledinterviews", "report", "profile",
]);
const DEFAULT_TAB = "InterviewerDashboard";
const TAB_KEY = "activeTab";
const RETURN_TAB_KEY = "returnTab";
const JDS_CACHE_PREFIX = "interviewerJDs:";

const readSession = (key) => {
  try { return sessionStorage.getItem(key); } catch { return null; }
};
const writeSession = (key, value) => {
  try { sessionStorage.setItem(key, value); } catch { /* storage unavailable */ }
};
const removeSession = (key) => {
  try { sessionStorage.removeItem(key); } catch { /* storage unavailable */ }
};

const readStoredUser = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem("rbotUser"));
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const readCachedJDs = (email) => {
  if (!email) return [];
  try {
    const cached = JSON.parse(localStorage.getItem(`${JDS_CACHE_PREFIX}${email}`) || "[]");
    return Array.isArray(cached) ? cached : [];
  } catch {
    return [];
  }
};

// Union of JD lists, de-duplicated by _id (the interviewer JD endpoint returns
// one entry per interview, so the same JD can appear several times).
const mergeJDs = (...lists) => {
  const seen = new Map();
  lists.flat().forEach((jd) => {
    if (jd && jd._id && !seen.has(jd._id)) seen.set(jd._id, jd);
  });
  return [...seen.values()];
};

// "YYYY-MM-DD" from <input type="date"> as a LOCAL date (new Date(str) would
// parse it as UTC midnight and shift the day in non-UTC timezones).
const parseLocalDate = (value) => {
  const [y, m, d] = String(value).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const INTERVIEW_DURATION_MS = 60 * 60 * 1000;
const isInterviewPast = (interview, now = Date.now()) => {
  const t = new Date(interview?.dateAndTime).getTime();
  return Number.isFinite(t) && now > t + INTERVIEW_DURATION_MS;
};

// Display-only: "29 Oct 2026 · 11:00"; raw value if unparseable.
const pad2 = (n) => String(n).padStart(2, "0");
const formatDisplayDateTime = (raw) => {
  const d = new Date(raw);
  if (raw == null || raw === "" || Number.isNaN(d.getTime())) return raw ? String(raw) : "—";
  const date = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  return `${date} · ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

const LoadingBlock = ({ label }) => (
  <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={5} gap={1.5}>
    <CircularProgress sx={{ color: "#6750A4" }} />
    {label && (
      <Typography sx={{ fontFamily: FONT_BODY, color: "#8B7DB5", fontSize: TYPE.body, lineHeight: 1.5 }}>{label}</Typography>
    )}
  </Box>
);

const ErrorBlock = ({ message, onRetry }) => (
  <Box sx={{ textAlign: "center", py: 5 }}>
    <Typography sx={{ color: "#B3261E", fontFamily: FONT_BODY, fontSize: TYPE.body, lineHeight: 1.5, mb: onRetry ? 1.5 : 0 }}>
      {message}
    </Typography>
    {onRetry && <GhostButton onClick={onRetry}>Retry</GhostButton>}
  </Box>
);

// ─────────────────────────────────────────────────────────────────────────────
function InterviewerDashboard() {
  const location = useLocation();
  const navigate = useNavigate();

  // User fields come from navigation state (fresh login) or, after a refresh /
  // new tab / direct URL, from the profile Login.js persisted in localStorage.
  const currentUser = useMemo(
    () => (location.state?.email ? location.state : readStoredUser()) || {},
    [location.state]
  );
  const { email, name, phone, userType, _id } = currentUser;
  const userData = useMemo(
    () => (email ? { name: name || "", phone, email, userType, _id } : null),
    [name, phone, email, userType, _id]
  );

  // ── State ──────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(() => {
    const saved = readSession(TAB_KEY);
    return saved && PERSISTABLE_TABS.has(saved) ? saved : DEFAULT_TAB;
  });
  const [tabRefreshKey, setTabRefreshKey] = useState(0);
  const [showCandidateProfiles, setShowCandidateProfiles] = useState(false);
  const [selectedCandidateProfileId, setSelectedCandidateProfileId] = useState(null);
  const [selectedUserType, setSelectedUserType] = useState(null);
  const [selectedJD, setSelectedJD] = useState(null);
  const [isViewJdDetailsOpen, setIsViewJdDetailsOpen] = useState(false);
  const [interviews, setInterviews] = useState([]);
  const [interviewsLoading, setInterviewsLoading] = useState(true);
  const [interviewsError, setInterviewsError] = useState("");
  const [recruiterEmail, setRecruiterEmail] = useState(null);
  const [selectedJdID, setSelectedJdID] = useState(null);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [selectedCandidateEmail, setSelectedCandidateEmail] = useState("");
  const [interviewId, setInterviewId] = useState("");
  const [selectedJdId, setSelectedJdId] = useState("");
  const [selectedCandidateID, setSelectedCandidateID] = useState("");
  const [selectedCandidateName, setSelectedCandidateName] = useState("");
  const [selectedJobrole, setSelectedJobrole] = useState("");
  const [showCandidateData, setShowCandidateData] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [notifAnchor, setNotifAnchor] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [jdCandidatesMap, setJdCandidatesMap] = useState({});
  const [interviewFromDate, setInterviewFromDate] = useState("");
  const [interviewToDate, setInterviewToDate] = useState("");
  const [loggingOut, setLoggingOut] = useState(false);
  const [currentPage, setCurrentPage] = useState({
    jobDescriptions: 1,
    scheduledInterviews: 1,
  });

  // Guards setState after unmount (fetches resolving late).
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  // ── API URLs (stable references) ───────────────────────────────────────────
  const logoutApiUrl = process.env.REACT_APP_LOGOUT_API;
  const scheduledCandidateDetailsApiUrl = process.env.REACT_APP_SCHEDULED_CANDIDATE_DETAILS_API;
  const getJobDescriptiondata = process.env.REACT_APP_JOB_DESCRIPTION_DATA_API;
  const candidateProfileByJdIdApi = process.env.REACT_APP_CANDIDATE_PROFILE_BY_JDID_API;

  // ── JDs: login state (assigned JDs) or per-user cache, merged with fetch ───
  const [jds, setJds] = useState(() =>
    Array.isArray(location.state?.jds) ? location.state.jds : readCachedJDs(email)
  );
  const [jdsLoading, setJdsLoading] = useState(true);
  const [jdsError, setJdsError] = useState("");

  // ── Derived / memoized values ──────────────────────────────────────────────

  const notifications = useMemo(
    () =>
      jds
        .map((jd) => {
          const pending = jdCandidatesMap[jd._id] || [];
          return pending.length > 0
            ? { jdID: jd.jdID, jobRole: jd.jobRole, count: pending.length, jd }
            : null;
        })
        .filter(Boolean),
    [jds, jdCandidatesMap]
  );

  // Filter by local date range, then sort: upcoming soonest-first, then past
  // most-recent-first. Pagination is applied after this.
  const filteredInterviews = useMemo(() => {
    const from = interviewFromDate ? parseLocalDate(interviewFromDate) : null;
    const to = interviewToDate ? parseLocalDate(interviewToDate) : null;
    if (to) to.setHours(23, 59, 59, 999);
    const now = Date.now();
    return interviews
      .filter((row) => {
        if (!from && !to) return true;
        const d = new Date(row?.dateAndTime);
        if (Number.isNaN(d.getTime())) return false;
        if (from && d < from) return false;
        if (to && d > to) return false;
        return true;
      })
      .sort((a, b) => {
        const ta = new Date(a?.dateAndTime).getTime() || 0;
        const tb = new Date(b?.dateAndTime).getTime() || 0;
        const pa = isInterviewPast(a, now);
        const pb = isInterviewPast(b, now);
        if (pa !== pb) return pa ? 1 : -1;
        return pa ? tb - ta : ta - tb;
      });
  }, [interviews, interviewFromDate, interviewToDate]);

  const isInterviewDateFiltered = interviewFromDate || interviewToDate;

  const totalPages = useMemo(
    () => ({
      jobDescriptions: Math.max(1, Math.ceil(jds.length / ITEMS_PER_PAGE)),
      scheduledInterviews: Math.max(1, Math.ceil(filteredInterviews.length / ITEMS_PER_PAGE)),
    }),
    [jds.length, filteredInterviews.length]
  );

  // Pages clamped to the current number of pages (lists can shrink).
  const jdPage = Math.min(currentPage.jobDescriptions, totalPages.jobDescriptions);
  const interviewPage = Math.min(currentPage.scheduledInterviews, totalPages.scheduledInterviews);

  // Sorted paginated JDs — memoized to avoid re-sorting on every render
  const sortedPagedJDs = useMemo(() => {
    const start = (jdPage - 1) * ITEMS_PER_PAGE;
    return [...jds]
      .sort((a, b) => new Date(b.creation_time) - new Date(a.creation_time))
      .slice(start, start + ITEMS_PER_PAGE);
  }, [jds, jdPage]);

  const pagedInterviews = useMemo(() => {
    const start = (interviewPage - 1) * ITEMS_PER_PAGE;
    return filteredInterviews.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredInterviews, interviewPage]);

  // ── API calls ──────────────────────────────────────────────────────────────

  const fetchJobDetails = useCallback(async () => {
    if (!email) return;
    setJdsLoading(true);
    try {
      const response = await axios.get(
        `${getJobDescriptiondata}/${encodeURIComponent(email)}`
      );
      if (!aliveRef.current) return;
      const fetched = Array.isArray(response.data) ? response.data : [];
      // Keep the same array when nothing new arrived, so the pending-count
      // effect does not refire.
      setJds((prev) => {
        const merged = mergeJDs(prev, fetched);
        return merged.length === prev.length ? prev : merged;
      });
      setJdsError("");
    } catch (error) {
      console.error("[InterviewerDashboard] Failed to fetch job details:", error);
      if (aliveRef.current) setJdsError("Failed to load your job descriptions.");
    } finally {
      if (aliveRef.current) setJdsLoading(false);
    }
  }, [email, getJobDescriptiondata]);

  const fetchPendingCount = useCallback(async () => {
    if (!jds?.length) {
      setJdCandidatesMap({});
      setPendingCount(0);
      return;
    }
    try {
      const responses = await Promise.all(
        jds.map((jd) =>
          axios
            .get(`${candidateProfileByJdIdApi}/${encodeURIComponent(jd._id)}`)
            .then((res) => ({ jdId: jd._id, data: res.data }))
            .catch(() => ({ jdId: jd._id, data: [] }))
        )
      );
      if (!aliveRef.current) return;

      let totalPending = 0;
      const newMap = {};
      responses.forEach(({ jdId, data }) => {
        const pending = Array.isArray(data)
          ? data.filter((p) => PENDING_STATUSES.includes(p?.overallStatus))
          : [];
        newMap[jdId] = pending;
        totalPending += pending.length;
      });

      setJdCandidatesMap(newMap);
      setPendingCount(totalPending);
    } catch (error) {
      console.error("[InterviewerDashboard] Failed to fetch pending count:", error);
    }
  }, [jds, candidateProfileByJdIdApi]);

  const fetchScheduledInterviews = useCallback(async () => {
    if (!email) return;
    setInterviewsLoading(true);
    try {
      const response = await axios.get(scheduledCandidateDetailsApiUrl, {
        params: { email },
      });
      if (!aliveRef.current) return;
      setInterviews(Array.isArray(response.data) ? response.data : []);
      setInterviewsError("");
    } catch (error) {
      console.error("[InterviewerDashboard] scheduled interviews:", error);
      if (aliveRef.current) setInterviewsError("Failed to load scheduled interviews.");
    } finally {
      if (aliveRef.current) setInterviewsLoading(false);
    }
  }, [email, scheduledCandidateDetailsApiUrl]);

  // ── Effects ────────────────────────────────────────────────────────────────

  // Only a missing user sends you to login — not a missing location.state.
  useEffect(() => {
    if (!email) navigate("/login", { replace: true });
  }, [email, navigate]);

  // Persist the active tab so refresh / new tab returns to it.
  useEffect(() => {
    if (PERSISTABLE_TABS.has(activeTab)) writeSession(TAB_KEY, activeTab);
  }, [activeTab]);

  // Cache the assigned JDs from login per user, for refreshes.
  useEffect(() => {
    if (email && Array.isArray(location.state?.jds)) {
      try {
        localStorage.setItem(`${JDS_CACHE_PREFIX}${email}`, JSON.stringify(location.state.jds));
      } catch { /* storage unavailable */ }
    }
  }, [email, location.state]);

  // Fetch pending counts whenever JDs change
  useEffect(() => {
    fetchPendingCount();
  }, [fetchPendingCount]);

  // Initial load: JDs and scheduled interviews (always fresh, never cache-only).
  useEffect(() => {
    fetchJobDetails();
    fetchScheduledInterviews();
  }, [fetchJobDetails, fetchScheduledInterviews]);

  // Reset to page 1 when the date filter changes.
  useEffect(() => {
    setCurrentPage((p) => ({ ...p, scheduledInterviews: 1 }));
  }, [interviewFromDate, interviewToDate]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handlePrevPage = useCallback(
    (dataset, current) =>
      setCurrentPage((p) => ({ ...p, [dataset]: Math.max(current - 1, 1) })),
    []
  );

  const handleNextPage = useCallback(
    (dataset, current) =>
      setCurrentPage((p) => ({
        ...p,
        [dataset]: Math.min(current + 1, totalPages[dataset]),
      })),
    [totalPages]
  );

  const closeSubViews = useCallback(() => {
    setIsViewJdDetailsOpen(false);
    setShowCandidateProfiles(false);
    setShowCandidateData(false);
    setShowFeedbackForm(false);
  }, []);

  // Remember the tab a sub-view was opened from, so Back/Close returns there.
  const rememberReturnTab = useCallback(() => {
    if (PERSISTABLE_TABS.has(activeTab)) writeSession(RETURN_TAB_KEY, activeTab);
  }, [activeTab]);

  /**
   * Single in-app tab switch used by the sidebar, PendingData's Back and the
   * feedback form. Resets every sub-view and refetches that tab's data.
   */
  const switchTab = useCallback((tab) => {
    closeSubViews();
    setSelectedJD(null);
    removeSession(RETURN_TAB_KEY);
    if (tab === "InterviewerDashboard") {
      fetchJobDetails();
      fetchScheduledInterviews();
      fetchPendingCount();
    }
    if (tab === "scheduledinterviews") fetchScheduledInterviews();
    if (tab === "pendingData") setSelectedUserType(userType);
    setActiveTab(tab);
    // Remounts self-loading tab components even when the same item is re-clicked.
    setTabRefreshKey((k) => k + 1);
  }, [closeSubViews, fetchJobDetails, fetchScheduledInterviews, fetchPendingCount, userType]);

  const handleProfileTabClick = useCallback(() => switchTab("profile"), [switchTab]);

  const handleLogout = useCallback(async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    const em = email || localStorage.getItem("email");
    let tokens = {};
    try {
      tokens = JSON.parse(localStorage.getItem("userTokens")) || {};
    } catch {
      tokens = {};
    }
    const storedToken = localStorage.getItem("token");
    const token =
      (em && tokens[em]) || (storedToken && storedToken !== "null" ? storedToken : null);

    try {
      if (token) {
        await axios.post(
          logoutApiUrl,
          { token, email: em },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
    } catch (error) {
      console.error("[InterviewerDashboard] Logout failed:", error);
    } finally {
      if (em) delete tokens[em];
      try {
        localStorage.setItem("userTokens", JSON.stringify(tokens));
        localStorage.removeItem("token");
        localStorage.removeItem("email");
        localStorage.removeItem("rbotUser");
        if (em) localStorage.removeItem(`${JDS_CACHE_PREFIX}${em}`);
        // Legacy unscoped keys from older builds.
        localStorage.removeItem("userData");
        localStorage.removeItem("interviewerJDs");
        localStorage.removeItem("scheduledInterviews");
        localStorage.removeItem("activeTab");
      } catch { /* storage unavailable */ }
      removeSession(TAB_KEY);
      removeSession(RETURN_TAB_KEY);
      navigate("/login", { replace: true });
    }
  }, [email, loggingOut, logoutApiUrl, navigate]);

  const handleInterviewerDashboardClick = useCallback(
    () => switchTab("InterviewerDashboard"),
    [switchTab]
  );

  const handleViewCandidateProfiles = useCallback(
    (jdId) => {
      if (!jdId) return;
      rememberReturnTab();
      closeSubViews();
      setShowCandidateProfiles(true);
      setSelectedCandidateProfileId(jdId);
      setSelectedUserType(userType);
      setActiveTab("");
    },
    [rememberReturnTab, closeSubViews, userType]
  );

  const handleViewJD = useCallback((jd) => {
    if (!jd) return;
    rememberReturnTab();
    closeSubViews();
    setActiveTab("view-job-description");
    setIsViewJdDetailsOpen(true);
    setSelectedJD({ ...jd, userRole: "recruiter" });
  }, [rememberReturnTab, closeSubViews]);

  const handleViewCandidateData = useCallback((interview) => {
    const candidate = interview?.candidate;
    if (!candidate) return;
    rememberReturnTab();
    closeSubViews();
    setActiveTab("");
    // Scheduled-interview rows only populate candidateID/name/email on the
    // candidate, so the JD and recruiter come from the interview itself.
    setSelectedCandidate({
      ...candidate,
      jddetails_id: candidate.jddetails_id || interview.jddetails_id || null,
      recruiterEmail: candidate.recruiterEmail || interview.recruiter,
      interviewid: candidate.interviewid || interview._id,
    });
    setShowCandidateData(true);
  }, [rememberReturnTab, closeSubViews]);

  const handleScheduledInterviewsClick = useCallback(
    () => switchTab("scheduledinterviews"),
    [switchTab]
  );

  const handleOpenFeedbackForm = useCallback((interview) => {
    if (!interview?._id) return;
    setSelectedCandidateEmail(interview.candidate?.candidateEmail || "");
    setInterviewId(interview._id);
    setSelectedJdId(interview.jddetails_id?._id || "");
    setSelectedJdID(interview.jddetails_id?.jdID || "");
    setRecruiterEmail(interview.recruiter);
    setSelectedJobrole(interview.jddetails_id?.jobRole || "");
    setSelectedCandidateID(interview.candidate?.candidateID || "");
    setSelectedCandidateName(interview.candidate?.candidatename || "");
    setShowFeedbackForm(true);
  }, []);

  const handleCloseFeedbackForm = useCallback(() => {
    setShowFeedbackForm(false);
    setSelectedCandidateEmail("");
    setInterviewId("");
    setSelectedJdId("");
    // The form only opens from Scheduled Interviews; keep refresh landing there.
    writeSession(TAB_KEY, "scheduledinterviews");
  }, []);

  // FeedbackForm calls setActiveTab(...) after a successful submit: return to
  // the scheduled list and refetch it so the row shows as Completed.
  const handleFeedbackSubmitted = useCallback(
    () => switchTab("scheduledinterviews"),
    [switchTab]
  );

  // Back/Close from JD details, candidate profiles or candidate data: return
  // to the tab the sub-view was opened from.
  const handleClose = useCallback(() => {
    const returnTab = readSession(RETURN_TAB_KEY);
    removeSession(RETURN_TAB_KEY);
    setSelectedJD(null);
    closeSubViews();
    setActiveTab(returnTab && PERSISTABLE_TABS.has(returnTab) ? returnTab : DEFAULT_TAB);
  }, [closeSubViews]);

  const handlePendingCandidatesData = useCallback(
    () => switchTab("pendingData"),
    [switchTab]
  );

  const handleClearDateFilter = useCallback(() => {
    setInterviewFromDate("");
    setInterviewToDate("");
  }, []);

  const handleNotifOpen = useCallback(
    (e) => setNotifAnchor(e.currentTarget),
    []
  );
  const handleNotifClose = useCallback(() => setNotifAnchor(null), []);

  const handleNotifItemClick = useCallback(
    (jdId) => {
      handleViewCandidateProfiles(jdId);
      handleNotifClose();
    },
    [handleViewCandidateProfiles, handleNotifClose]
  );

  const handlePendingDataBack = useCallback(
    () => switchTab("InterviewerDashboard"),
    [switchTab]
  );

  // ─── RENDER ──────────────────────────────────────────────────────────────────
  return (
    <ThemeProvider theme={recruiterTheme}>
      <GlobalStyle />
      <Container>
        <CssBaseline />

        {/* ── AppBar ── */}
        <StyledAppBar position="fixed">
          <Toolbar style={TOOLBAR_STYLE}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                paddingRight: "24px",
                borderRight: "1.5px solid rgba(103,80,164,0.1)",
              }}
            >
              <Box sx={LOGO_BOX_SX}>
                <img src={logo} alt="Logo" style={LOGO_IMG_STYLE} />
              </Box>
              <Box>
                <BrandText>Resourcing Bot</BrandText>
                <Typography sx={PLATFORM_LABEL_SX}>
                  Recruitment Platform
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <BellButton onClick={handleNotifOpen}>
                <NotificationsIcon />
                {notifications.length > 0 && (
                  <Box sx={NOTIF_BADGE_SX}>
                    {notifications.length > 99 ? "99+" : notifications.length}
                  </Box>
                )}
              </BellButton>

              <Menu
                anchorEl={notifAnchor}
                open={Boolean(notifAnchor)}
                onClose={handleNotifClose}
                PaperProps={{ sx: NOTIF_MENU_PAPER_SX }}
              >
                <NotifHeader>🔔 Notifications</NotifHeader>
                {notifications.length === 0 ? (
                  <Box sx={NOTIF_EMPTY_SX}>No new notifications</Box>
                ) : (
                  notifications.map((n, i) => (
                    <NotifItem
                      key={i}
                      onClick={() => handleNotifItemClick(n.jd._id)}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1.5,
                          alignItems: "flex-start",
                          width: "100%",
                        }}
                      >
                        <NotifDot />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            title={`${n.jdID} — ${n.jobRole}`}
                            sx={{
                              fontFamily: FONT_BODY,
                              fontWeight: 600,
                              fontSize: TYPE.small,
                              lineHeight: 1.5,
                              color: "#2D1B5E",
                              ...ELLIPSIS,
                            }}
                          >
                            {n.jdID} — {n.jobRole}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: FONT_BODY,
                              fontSize: TYPE.caption,
                              lineHeight: 1.5,
                              color: "#8B7DB5",
                            }}
                          >
                            {n.count} candidate(s) waiting for review
                          </Typography>
                        </Box>
                      </Box>
                    </NotifItem>
                  ))
                )}
              </Menu>

              {userData && (
                <UserCard>
                  <StyledAvatar>{(userData.name || userData.email || "?").charAt(0).toUpperCase()}</StyledAvatar>
                  <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontFamily: FONT_BODY,
                        fontWeight: 600,
                        fontSize: TYPE.small,
                        color: "#2D1B5E",
                        lineHeight: 1.25,
                      }}
                    >
                      {userData.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: FONT_BODY,
                        fontWeight: 500,
                        fontSize: TYPE.caption,
                        color: "#8B7DB5",
                        lineHeight: 1.25,
                        textTransform: "capitalize",
                      }}
                    >
                      {userData.userType}
                    </Typography>
                  </Box>
                </UserCard>
              )}
            </Box>
          </Toolbar>
        </StyledAppBar>

        {/* ── Sidebar ── */}
        <MenuContainer>
          <SidebarLabel>Interviewer Tools</SidebarLabel>

          <NavButton
            $active={activeTab === "InterviewerDashboard"}
            onClick={handleInterviewerDashboardClick}
          >
            <WidgetsIcon />
            <span className="nav-label">Dashboard</span>
          </NavButton>

          <NavButton
            $active={activeTab === "pendingData"}
            onClick={handlePendingCandidatesData}
          >
            <PendingActionsIcon />
            <span className="nav-label">Pending Actions</span>
            {pendingCount > 0 && (
              <NavBadge>{pendingCount > 99 ? "99+" : pendingCount}</NavBadge>
            )}
          </NavButton>

          <NavButton
            $active={activeTab === "scheduledinterviews"}
            onClick={handleScheduledInterviewsClick}
          >
            <AccessTimeIcon />
            <span className="nav-label">Scheduled Interviews</span>
          </NavButton>

          <SidebarDivider />
          <SidebarLabel>Reporting</SidebarLabel>

          <NavButton
            $active={activeTab === "report"}
            onClick={() => switchTab("report")}
          >
            <BarChartIcon />
            <span className="nav-label">Reports</span>
          </NavButton>

          <SidebarDivider />
          <SidebarLabel>Account</SidebarLabel>

          <NavButton
            $active={activeTab === "profile"}
            onClick={handleProfileTabClick}
          >
            <PersonIcon />
            <span className="nav-label">Profile</span>
          </NavButton>

          <NavButton $danger disabled={loggingOut} onClick={handleLogout}>
            {loggingOut ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : <ExitToAppIcon />}
            <span className="nav-label">{loggingOut ? "Logging out…" : "Logout"}</span>
          </NavButton>
        </MenuContainer>

        {/* ── Main Content ── */}
        <CenteredSection>
          <ScrollableContent>

            {/* ── Interviewer Dashboard ── */}
            {activeTab === "InterviewerDashboard" && (
              <StyledPaper>
                <PageTitle>Interviewer Dashboard</PageTitle>
                <PageSubtitle>
                  View assigned job descriptions and manage your interview
                  schedule
                </PageSubtitle>

                <StatGrid>
                  <StatCard
                    gradient="linear-gradient(135deg, #6750A4, #4F378B)"
                    shadow="rgba(103,80,164,0.32)"
                  >
                    <StatText>
                      <StatNumber>{jds.length}</StatNumber>
                      <StatLabel>Assigned JDs</StatLabel>
                    </StatText>
                    <StatIcon>
                      <WidgetsIcon />
                    </StatIcon>
                  </StatCard>
                  <StatCard
                    gradient="linear-gradient(135deg, #E91E8C, #C2185B)"
                    shadow="rgba(233,30,140,0.28)"
                  >
                    <StatText>
                      <StatNumber>{pendingCount}</StatNumber>
                      <StatLabel>Pending</StatLabel>
                    </StatText>
                    <StatIcon>
                      <PendingActionsIcon />
                    </StatIcon>
                  </StatCard>
                  <StatCard
                    gradient="linear-gradient(135deg, #00897B, #00695C)"
                    shadow="rgba(0,137,123,0.28)"
                  >
                    <StatText>
                      <StatNumber>
                        {interviewsLoading && interviews.length === 0 ? "…" : interviews.length}
                      </StatNumber>
                      <StatLabel>Scheduled</StatLabel>
                    </StatText>
                    <StatIcon>
                      <AccessTimeIcon />
                    </StatIcon>
                  </StatCard>
                </StatGrid>

                {jds.length === 0 && jdsLoading ? (
                  <LoadingBlock label="Loading job descriptions…" />
                ) : jds.length === 0 && jdsError ? (
                  <ErrorBlock message={jdsError} onRetry={fetchJobDetails} />
                ) : jds.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 5 }}>
                    <Typography sx={EMPTY_TEXT_SX}>
                      No job descriptions assigned yet.
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <SectionHeader>
                      <SectionAccent />
                      <Typography sx={SECTION_TITLE_SX}>
                        Assigned Job Descriptions
                      </Typography>
                    </SectionHeader>
                    <TableCard>
                      <TableScroll>
                        <StyledTable>
                          <TableHead>
                            <TableRow>
                              <HeadCell>JD ID</HeadCell>
                              <HeadCell>Job Role</HeadCell>
                              <HeadCell sx={{ textAlign: "right" }}>Candidates</HeadCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sortedPagedJDs.map((jd) => (
                              <BodyRow key={jd._id}>
                                <BodyCell sx={{ whiteSpace: "nowrap", width: 140 }}>
                                  <LinkButton onClick={() => handleViewJD(jd)}>
                                    {jd.jdID}
                                  </LinkButton>
                                </BodyCell>
                                <BodyCell sx={{ fontWeight: "600 !important" }}>
                                  <CellClamp minWidth={220} title={jd.jobRole || undefined}>
                                    {jd.jobRole}
                                  </CellClamp>
                                </BodyCell>
                                <BodyCell sx={{ textAlign: "right", whiteSpace: "nowrap" }}>
                                  <GhostButton
                                    onClick={() =>
                                      handleViewCandidateProfiles(jd._id)
                                    }
                                  >
                                    View Candidates
                                  </GhostButton>
                                </BodyCell>
                              </BodyRow>
                            ))}
                          </TableBody>
                        </StyledTable>
                      </TableScroll>
                    </TableCard>
                    {totalPages.jobDescriptions > 1 && (
                      <Pagination
                        itemsPerPage={ITEMS_PER_PAGE}
                        currentPage={jdPage}
                        totalPages={totalPages.jobDescriptions}
                        handlePrevPage={() => handlePrevPage("jobDescriptions", jdPage)}
                        handleNextPage={() => handleNextPage("jobDescriptions", jdPage)}
                      />
                    )}
                  </>
                )}
              </StyledPaper>
            )}

            {/* ── Scheduled Interviews ── */}
            {activeTab === "scheduledinterviews" &&
              !showFeedbackForm &&
              !isViewJdDetailsOpen && (
                <StyledPaper>
                  <PageTitle>Scheduled Interviews</PageTitle>
                  <PageSubtitle>
                    Your upcoming interview sessions and feedback actions
                  </PageSubtitle>

                  <StatGrid $fill>
                    <StatCard
                      gradient="linear-gradient(135deg, #00897B, #00695C)"
                      shadow="rgba(0,137,123,0.28)"
                    >
                      <StatText>
                        <StatNumber>{filteredInterviews.length}</StatNumber>
                        <StatLabel>Interviews</StatLabel>
                      </StatText>
                      <StatIcon>
                        <AccessTimeIcon />
                      </StatIcon>
                    </StatCard>
                  </StatGrid>

                  <SectionHeader>
                    <SectionAccent />
                    <Typography sx={SECTION_TITLE_SX}>
                      Interview Sessions
                    </Typography>
                  </SectionHeader>

                  <TableCard>
                    {/* ── Date Range Filter ── */}
                    <CardToolbar>
                      <FilterLabel>📅 Filter by date</FilterLabel>
                      <FilterField>
                        <FilterLabel style={FILTER_SUBLABEL_STYLE}>From</FilterLabel>
                        <DateInput
                          type="date"
                          value={interviewFromDate}
                          max={interviewToDate || undefined}
                          onChange={(e) => setInterviewFromDate(e.target.value)}
                        />
                      </FilterField>
                      <FilterField>
                        <FilterLabel style={FILTER_SUBLABEL_STYLE}>To</FilterLabel>
                        <DateInput
                          type="date"
                          value={interviewToDate}
                          min={interviewFromDate || undefined}
                          onChange={(e) => setInterviewToDate(e.target.value)}
                        />
                      </FilterField>
                      {isInterviewDateFiltered && (
                        <>
                          <FilterResultPill>
                            {filteredInterviews.length} interview
                            {filteredInterviews.length !== 1 ? "s" : ""}
                          </FilterResultPill>
                          <ClearFilterBtn onClick={handleClearDateFilter}>
                            ✕ Clear
                          </ClearFilterBtn>
                        </>
                      )}
                    </CardToolbar>

                    {interviews.length === 0 && interviewsLoading ? (
                      <LoadingBlock label="Loading scheduled interviews…" />
                    ) : interviews.length === 0 && interviewsError ? (
                      <ErrorBlock message={interviewsError} onRetry={fetchScheduledInterviews} />
                    ) : filteredInterviews.length === 0 ? (
                      <Box sx={{ textAlign: "center", py: 5 }}>
                        <Typography sx={EMPTY_TEXT_SX}>
                          {isInterviewDateFiltered
                            ? "No interviews found in this date range."
                            : "No scheduled interviews found."}
                        </Typography>
                      </Box>
                    ) : (
                      <TableScroll>
                        <StyledTable>
                          <TableHead>
                            <TableRow>
                              <HeadCell>JD Details</HeadCell>
                              <HeadCell>Candidate</HeadCell>
                              <HeadCell>Recruiter Email</HeadCell>
                              <HeadCell>Meet Link</HeadCell>
                              <HeadCell>Interview Date</HeadCell>
                              <HeadCell sx={{ textAlign: "right" }}>Feedback</HeadCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {pagedInterviews.map((interview) => {
                              const isExpired = isInterviewPast(interview);
                              const isCompleted =
                                interview.interviewStatus === "Completed";
                              const jobRole = interview.jddetails_id?.jobRole;
                              const candidateEmail = interview.candidate?.candidateEmail;

                              return (
                                <BodyRow key={interview._id}>
                                  <BodyCell>
                                    <Box sx={{ whiteSpace: "nowrap" }}>
                                      {interview.jddetails_id ? (
                                        <LinkButton onClick={() => handleViewJD(interview.jddetails_id)}>
                                          {interview.jddetails_id.jdID}
                                        </LinkButton>
                                      ) : "—"}
                                    </Box>
                                    {jobRole && (
                                      <CellClamp
                                        minWidth={160}
                                        maxWidth={220}
                                        title={jobRole}
                                        style={{ fontWeight: 600 }}
                                      >
                                        {jobRole}
                                      </CellClamp>
                                    )}
                                  </BodyCell>
                                  <BodyCell>
                                    <Box sx={{ whiteSpace: "nowrap" }}>
                                      <LinkButton
                                        onClick={() =>
                                          handleViewCandidateData(interview)
                                        }
                                        disabled={!interview.candidate}
                                      >
                                        {interview.candidate?.candidateID || "—"}
                                      </LinkButton>
                                    </Box>
                                    {candidateEmail && (
                                      <CellEllipsis
                                        maxWidth={200}
                                        title={candidateEmail}
                                        style={{ fontSize: TYPE.caption, color: "#8B7DB5", lineHeight: 1.5 }}
                                      >
                                        {candidateEmail}
                                      </CellEllipsis>
                                    )}
                                  </BodyCell>
                                  <BodyCell sx={MUTED_CELL_SX}>
                                    <CellEllipsis maxWidth={200} title={interview.recruiter || undefined}>
                                      {interview.recruiter || "—"}
                                    </CellEllipsis>
                                  </BodyCell>
                                  <BodyCell sx={{ whiteSpace: "nowrap" }}>
                                    {!isCompleted &&
                                    interview.meet_link &&
                                    !isExpired ? (
                                      <JoinLink
                                        href={interview.meet_link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        Join
                                      </JoinLink>
                                    ) : isExpired || isCompleted ? (
                                      <ExpiredChip>Expired</ExpiredChip>
                                    ) : (
                                      "—"
                                    )}
                                  </BodyCell>
                                  <BodyCell sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                                    {formatDisplayDateTime(interview.dateAndTime)}
                                  </BodyCell>
                                  <BodyCell sx={{ textAlign: "right" }}>
                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}>
                                      {isCompleted ? (
                                        <CompletedChip>Completed</CompletedChip>
                                      ) : (
                                        <GhostButton
                                          onClick={() =>
                                            handleOpenFeedbackForm(interview)
                                          }
                                        >
                                          Give Feedback
                                        </GhostButton>
                                      )}
                                    </Box>
                                  </BodyCell>
                                </BodyRow>
                              );
                            })}
                          </TableBody>
                        </StyledTable>
                      </TableScroll>
                    )}
                  </TableCard>
                  {filteredInterviews.length > 0 && totalPages.scheduledInterviews > 1 && (
                    <Pagination
                      itemsPerPage={ITEMS_PER_PAGE}
                      currentPage={interviewPage}
                      totalPages={totalPages.scheduledInterviews}
                      handlePrevPage={() =>
                        handlePrevPage("scheduledInterviews", interviewPage)
                      }
                      handleNextPage={() =>
                        handleNextPage("scheduledInterviews", interviewPage)
                      }
                    />
                  )}
                </StyledPaper>
              )}

            {/* ── Pending Data (embedded via onBack: adds no padding itself) ── */}
            {activeTab === "pendingData" && userData && (
              <StyledPaper>
                <PendingData
                  key={tabRefreshKey}
                  email={userData.email}
                  userType={selectedUserType || userType}
                  onBack={handlePendingDataBack}
                />
              </StyledPaper>
            )}

            {/* ── Profile (pads itself) ── */}
            {activeTab === "profile" && userData && <ProfileForm userData={userData} />}

            {/* ── Reports ── */}
            {activeTab === "report" && (
              <StyledPaper>
                <PageTitle>Reports</PageTitle>
                <PageSubtitle>
                  Analytics and insights for your interview pipeline
                </PageSubtitle>
                <Reports key={tabRefreshKey} />
              </StyledPaper>
            )}

            {/* ── Sub-pages ── */}
            {/* CandidateProfiles / CandidateData given onBack render embedded (no padding). */}
            {showCandidateProfiles && selectedCandidateProfileId && (
              <StyledPaper>
                <CandidateProfiles
                  jdId={selectedCandidateProfileId}
                  userType={selectedUserType || userType}
                  onBack={handleClose}
                />
              </StyledPaper>
            )}

            {/* JDDetails and FeedbackForm pad themselves. */}
            {isViewJdDetailsOpen && (
              <JDDetails
                jdDetails={selectedJD}
                userRole="interviewer"
                interviewerEmail={userData?.email}
                onBack={handleClose}
              />
            )}

            {showFeedbackForm && (
              <StyledPaper>
              <FeedbackForm
                showFeedbackForm={showFeedbackForm}
                onCloseFeedbackForm={handleCloseFeedbackForm}
                selectedCandidateEmail={selectedCandidateEmail}
                selectedInterviewId={interviewId}
                selectedJdId={selectedJdId}
                recruiterEmail={recruiterEmail}
                selectedJdID={selectedJdID}
                selectedJobRole={selectedJobrole}
                selectedCandidateID={selectedCandidateID}
                selectedCandidateName={selectedCandidateName}
                activeTab={activeTab}
                setActiveTab={handleFeedbackSubmitted}
              />
              </StyledPaper>
            )}

            {showCandidateData && selectedCandidate && (
              <StyledPaper>
                <CandidateData
                  candidateId={selectedCandidate._id}
                  userRole={userType}
                  candidateEmail={selectedCandidate.candidateEmail || undefined}
                  candidateName={selectedCandidate.candidatename || undefined}
                  overallStatus={selectedCandidate.overallStatus || undefined}
                  jdId={selectedCandidate.jddetails_id?.jdID || undefined}
                  JobRole={selectedCandidate.jddetails_id?.jobRole || undefined}
                  jobDescription={
                    selectedCandidate.jddetails_id?.jobDescription || undefined
                  }
                  salaryRange={
                    selectedCandidate.jddetails_id?.salaryRange || undefined
                  }
                  companyName={
                    selectedCandidate.jddetails_id?.companyName || undefined
                  }
                  recruiterEmail={selectedCandidate.recruiterEmail || undefined}
                  skills={selectedCandidate.skills || undefined}
                  experience={selectedCandidate.experience || undefined}
                  interviewid={selectedCandidate.interviewid || undefined}
                  onBack={handleClose}
                />
              </StyledPaper>
            )}
          </ScrollableContent>
        </CenteredSection>
      </Container>
    </ThemeProvider>
  );
}

export default InterviewerDashboard;